import { clsx } from 'clsx'
import { CircleButton } from 'component/Button/CircleButton'
import { IconButton } from 'component/Button/IconButton'
import { CarouselStore } from 'component/Carousel/store/CarouselStore'
import { Space } from 'component/Space'
import { Gap } from 'component/Space/Gap'
import { Vertical } from 'component/Vertical'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { Actions } from 'page/InferenceScreen/Actions'
import { Image } from 'page/InferenceScreen/Carousel/Image'
import { Video } from 'page/InferenceScreen/Carousel/Video'
import { MouseEvent, useEffect, useRef, useState } from 'react'
import { FailedBottom } from 'shared/inference/FailedBottom'
import { ProcessingCard } from 'shared/inference/ProcessingCard'
import { controller } from 'store/controller'
import { InferenceStore } from 'store/inference/InferenceStore'
import { opt } from 'store/opt'
import { Size } from 'util/size'
import css from './Card2.module.scss'

type Props = {
  carousel: CarouselStore<InferenceStore>
  inference: InferenceStore
  onClick?: () => void
}

const Failed = observer(() => {
  return (
    <Vertical wide tall center>
      <Vertical className={css.empty} center>
        <Icon name="error" size={opt.stand ? 64 : 48} error />
        <Gap height={opt.stand ? 40 : 20} />
        <FailedBottom />
      </Vertical>
    </Vertical>
  )
})

const Done = observer(({ carousel, inference }: Props) => {
  const { item } = carousel
  const source = inference.sources[0]
  const video = inference.videos[0]?.src
  const is_video = !!video
  const active = inference === item
  const empty = !video && !source

  const videoRef = useRef<HTMLVideoElement>(null)
  const [goodVideo, setGoodVideo] = useState(false)
  const [, setSizeVideo] = useState<Size>()
  const [goodImage, setGoodImage] = useState(false)
  const [, setSizeImage] = useState<Size>()

  const [touch, setTouch] = useState(false)
  const [rewind, setRewind] = useState(false)
  const [play, setPlay] = useState(false)

  const className = clsx(css.card, !goodVideo && !goodImage && css.blank)

  useEffectOnce(() => {
    const element = videoRef.current
    if (!element) return

    element.load()
    element.addEventListener('play', () => {
      setTouch(true)
      setRewind(false)
      setPlay(true)
    })
    element.addEventListener('pause', () => setPlay(false))
    element.addEventListener('ended', () => {
      element.currentTime = 0
      setRewind(true)
    })
  })

  useEffect(() => {
    if (!active) {
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.currentTime = 0
      }
      setTouch(false)
      setRewind(false)
      setPlay(false)
    }
  }, [active])

  const onImageRef = useLatestCallback(
    (element: HTMLImageElement | undefined | null) => {
      inference.image_element = element || undefined
    },
  )

  const onButton = useLatestCallback((event: MouseEvent) => {
    event.stopPropagation()
  })

  const onPlay = useLatestCallback(() => {
    setTimeout(() => {
      if (!videoRef.current) return
      if (play) void videoRef.current.pause()
      else void videoRef.current.play()
    }, 50)
  })

  if (empty) return <Vertical className={clsx(css.card, css.blank)}></Vertical>

  return (
    <div className={className}>
      {is_video && (
        <Video
          videoRef={videoRef}
          inference={inference}
          onGood={setGoodVideo}
          onSize={setSizeVideo}
        />
      )}
      {(!is_video || !touch) && (
        <Image
          imageRef={onImageRef}
          inference={inference}
          preview={is_video && goodVideo}
          onGood={setGoodImage}
          onSize={setSizeImage}
        />
      )}

      <div
        className={clsx(css.actions, active && css.active)}
        onClick={onButton}
      >
        {is_video && (
          <Space gap={8} pin={[0, 0, 0, 0]} center>
            <IconButton
              className={clsx(css.play, play && css.busy)}
              action={onPlay}
            >
              <Icon
                size={24}
                fill
                name={rewind ? 'refresh' : play ? 'pause' : 'play_arrow'}
              />
            </IconButton>
          </Space>
        )}
      </div>
    </div>
  )
})

export const Card2 = observer((props: Props) => {
  const { carousel, inference, onClick } = props
  const { item } = carousel
  const { error, complete } = inference
  const active = inference === item
  const actions = !opt.stand && inference.complete

  let content
  if (error) content = <Failed />
  else if (!complete) content = <ProcessingCard inference={inference} animated />
  else content = <Done {...props} />

  const onClickInner = useLatestCallback(() => {
    carousel.item = inference
    if (onClick) onClick()
  })

  const onClose = useLatestCallback(() => {
    controller.closeFullscreen()
  })

  return <Space className={css.root} top onClick={onClickInner}>
    {content}
    {active && (
      <Vertical pin={[8, 8, null, null]} gap={8}>
        <CircleButton size={40} action={onClose}>
          <Icon size={20} name="close" />
        </CircleButton>
        {actions && <Actions inference={inference} />}
      </Vertical>
    )}
  </Space>
})
